import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

// Imported calendars. Drop in an .ics export (Outlook can't be shared by link, so it gets
// exported now and then); re-importing the same calendar replaces its events. Busy events
// also block time on the Daily Plan.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STALE_DAYS = 21;

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
const fmtTime = (d) => new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDate = (d) => new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' });

// Local day keys an event touches (all-day by date, timed by local clock).
const eventDayKeys = (e) => {
  const keys = [];
  if (e.allDay) {
    let d = new Date(`${e.startDay}T12:00:00`);
    const end = new Date(`${e.endDay}T12:00:00`);
    while (d < end && keys.length < 62) { keys.push(toKey(d)); d = addDays(d, 1); }
    return keys;
  }
  const s = new Date(e.start);
  const end = new Date(e.end);
  let d = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  do { keys.push(toKey(d)); d = addDays(d, 1); } while (d < end && keys.length < 62);
  return keys;
};

const eventTimeLabel = (e) => {
  if (e.allDay) {
    const days = eventDayKeys(e).length;
    return days > 1 ? `All day · ${fmtDate(`${e.startDay}T12:00:00`)} to ${fmtDate(addDays(new Date(`${e.endDay}T12:00:00`), -1))}` : 'All day';
  }
  return `${fmtTime(e.start)} to ${fmtTime(e.end)}`;
};

const agoText = (d) => {
  const mins = Math.round((new Date() - new Date(d)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} hr ago`;
  return `${Math.round(mins / 1440)} days ago`;
};

const coverage = (cal) => {
  // Google calendars are pushed by the workstation sync job every 15 minutes.
  if (cal.source === 'google') {
    const stale = (new Date() - new Date(cal.importedAt)) > 3 * 3600e3;
    return { stale, google: true, text: `Google · synced ${agoText(cal.importedAt)}` };
  }
  if (!cal.rangeEnd) return { stale: false, text: '' };
  const daysLeft = Math.round((new Date(cal.rangeEnd) - new Date()) / 864e5);
  const ageDays = Math.round((new Date() - new Date(cal.importedAt)) / 864e5);
  const stale = daysLeft < STALE_DAYS || ageDays > 30;
  return { stale, text: `Imported ${fmtDate(cal.importedAt)} · covers through ${fmtDate(addDays(new Date(cal.rangeEnd), -1))}` };
};

const Calendar = () => {
  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selectedKey, setSelectedKey] = useState(() => toKey(new Date()));
  const [openEvent, setOpenEvent] = useState(null);
  const [status, setStatus] = useState(null);       // { type: 'ok'|'error'|'busy', text }
  const [dragging, setDragging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);

  const todayKey = toKey(new Date());

  // 6-week grid starting on the Sunday on/before the 1st.
  const gridDays = useMemo(() => {
    const first = addDays(month, -month.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [month]);

  const loadCalendars = useCallback(async () => {
    try {
      const res = await axios.get('/api/calendar/calendars');
      setCalendars(res.data);
    } catch (e) { /* page still renders */ }
  }, []);

  const loadEvents = useCallback(async () => {
    const from = gridDays[0];
    const to = addDays(gridDays[gridDays.length - 1], 1);
    try {
      const res = await axios.get('/api/calendar/events', { params: { from: from.toISOString(), to: to.toISOString() } });
      setEvents(res.data);
    } catch (e) {
      setEvents([]);
    }
  }, [gridDays]);

  useEffect(() => { loadCalendars(); }, [loadCalendars]);
  useEffect(() => { loadEvents(); }, [loadEvents, calendars]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => eventDayKeys(e).forEach((k) => { (map[k] = map[k] || []).push(e); }));
    Object.values(map).forEach((list) => list.sort((a, b) => (b.allDay - a.allDay) || (new Date(a.start) - new Date(b.start))));
    return map;
  }, [events]);

  const importFile = async (file) => {
    if (!file) return;
    if (!/\.ics$/i.test(file.name)) { setStatus({ type: 'error', text: 'Pick an .ics calendar export.' }); return; }
    setStatus({ type: 'busy', text: `Importing ${file.name}…` });
    const body = new FormData();
    body.append('file', file);
    try {
      const res = await axios.post('/api/calendar/import', body);
      const { calendar, imported, replaced } = res.data;
      setStatus({ type: 'ok', text: `${replaced ? 'Updated' : 'Added'} "${calendar.name}" with ${imported} events.` });
      await loadCalendars();
    } catch (e) {
      setStatus({ type: 'error', text: e.response?.data?.msg || 'Import failed.' });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const updateCalendar = async (id, patch) => {
    setCalendars((cur) => cur.map((c) => (c._id === id ? { ...c, ...patch } : c)));
    try {
      await axios.patch(`/api/calendar/calendars/${id}`, patch);
      if (patch.color) loadEvents();
    } catch (e) {
      loadCalendars();
    }
  };

  const deleteCalendar = async (id) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`/api/calendar/calendars/${id}`);
      await loadCalendars();
    } catch (e) {
      setStatus({ type: 'error', text: 'Could not remove that calendar.' });
    }
  };

  const selectedEvents = byDay[selectedKey] || [];
  const selectedDate = new Date(`${selectedKey}T12:00:00`);

  return (
    <div
      className="container mx-auto px-4 py-6"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); importFile(e.dataTransfer.files?.[0]); }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-gray-900 dark:text-gray-100 uppercase">Calendar</h1>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">Outlook exports + Google calendars · busy events block your Daily Plan</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".ics,text/calendar" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
          <button
            onClick={() => fileRef.current?.click()}
            className="px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs text-white bg-primary-blue hover:opacity-90 transition-all">
            Import .ics
          </button>
        </div>
      </div>

      {dragging && (
        <div className="mb-4 rounded-2xl border-2 border-dashed border-primary-blue bg-primary-blue/5 py-8 text-center text-sm font-bold text-primary-blue pointer-events-none">
          Drop the .ics file to import
        </div>
      )}

      {status && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm font-semibold flex items-center justify-between gap-3 ${status.type === 'error' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300' : status.type === 'ok' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
          <span>{status.text}</span>
          {status.type !== 'busy' && <button onClick={() => setStatus(null)} className="text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100">Dismiss</button>}
        </div>
      )}

      {/* Calendars */}
      {calendars.length === 0 ? (
        <div className="mb-6 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-8 text-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">No calendars yet.</p>
          <p className="text-xs text-gray-400 mt-1">In Outlook: File, Save Calendar, pick a date range, then drop the .ics file here. Re-import it anytime to refresh.</p>
        </div>
      ) : (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {calendars.map((c) => {
            const cov = coverage(c);
            return (
              <div key={c._id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 px-4 py-3">
                <div className="flex items-center gap-3">
                  <label className="relative h-4 w-4 rounded-full flex-shrink-0 cursor-pointer ring-2 ring-white dark:ring-gray-900 shadow" style={{ background: c.color }} title="Change color">
                    <input type="color" value={c.color} onChange={(e) => updateCalendar(c._id, { color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{c.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">{cov.text}{cov.google ? '' : ` · ${c.eventCount} events`}</div>
                  </div>
                  {cov.google ? null : confirmDelete === c._id ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => deleteCalendar(c._id)} className="text-[11px] font-bold uppercase tracking-widest text-red-500 px-2 py-1">Remove</button>
                      <button onClick={() => setConfirmDelete(null)} className="text-[11px] font-bold uppercase tracking-widest text-gray-400 px-2 py-1">Keep</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(c._id)} title="Remove calendar" className="p-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <label className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={c.blocksDayPlan} onChange={(e) => updateCalendar(c._id, { blocksDayPlan: e.target.checked })} className="h-3.5 w-3.5 accent-primary-blue" />
                    Blocks Daily Plan
                  </label>
                  {cov.stale && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-full px-2 py-0.5">{cov.google ? 'Sync stalled' : 'Time to re-export'}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Month grid */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="px-2 py-1 text-gray-400 hover:text-primary-blue font-black" aria-label="Previous month">‹</button>
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-100">
                {month.toLocaleDateString([], { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); setSelectedKey(toKey(n)); }}
                className="text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-primary-blue border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-0.5">
                Today
              </button>
            </div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="px-2 py-1 text-gray-400 hover:text-primary-blue font-black" aria-label="Next month">›</button>
          </div>
          <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-800">
            {DOW.map((d) => <div key={d} className="py-2 text-center text-[10px] font-black uppercase tracking-widest text-gray-400">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {gridDays.map((d) => {
              const key = toKey(d);
              const list = byDay[key] || [];
              const inMonth = d.getMonth() === month.getMonth();
              const selected = key === selectedKey;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedKey(key)}
                  className={`flex flex-col items-stretch justify-start min-h-[4.5rem] sm:min-h-[6.5rem] text-left p-1 sm:p-1.5 border-b border-r border-gray-50 dark:border-gray-800 transition-colors ${selected ? 'bg-primary-blue/5' : 'hover:bg-gray-50/60 dark:hover:bg-gray-800/40'} ${inMonth ? '' : 'opacity-40'}`}>
                  <div className={`text-xs font-black mb-1 w-6 h-6 flex items-center justify-center rounded-full ${key === todayKey ? 'bg-primary-blue text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                    {d.getDate()}
                  </div>
                  {/* Phones: dots. Wider: chips. */}
                  <div className="flex flex-wrap gap-0.5 sm:hidden">
                    {list.slice(0, 4).map((e) => <span key={e._id} className="h-1.5 w-1.5 rounded-full" style={{ background: e.color }} />)}
                  </div>
                  <div className="hidden sm:block space-y-0.5">
                    {list.slice(0, 3).map((e) => (
                      <div
                        key={e._id}
                        onClick={(ev) => { ev.stopPropagation(); setOpenEvent(e); }}
                        className={`truncate rounded px-1 py-px text-[10px] font-semibold leading-tight ${e.allDay ? 'text-white' : 'text-gray-700 dark:text-gray-200'}`}
                        style={e.allDay ? { background: e.color } : { borderLeft: `3px solid ${e.color}`, background: `${e.color}14` }}>
                        {!e.allDay && <span className="tabular-nums text-gray-400 mr-1">{fmtTime(e.start).replace(':00', '').replace(' ', '').toLowerCase()}</span>}
                        {e.title}
                      </div>
                    ))}
                    {list.length > 3 && <div className="text-[10px] font-bold text-gray-400 px-1">+{list.length - 3} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day agenda */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-100">
                {selectedDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
            </div>
            {selectedEvents.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400">Nothing on the calendar.</p>
            ) : (
              <div>
                {selectedEvents.map((e) => (
                  <button key={e._id} onClick={() => setOpenEvent(e)} className="w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50/60 dark:hover:bg-gray-800/40 flex gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: e.color }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">{e.title}</span>
                      <span className="block text-[11px] text-gray-400">
                        {eventTimeLabel(e)}
                        {e.busy === 'tentative' && ' · tentative'}
                        {e.busy === 'free' && ' · free'}
                        {e.location && ` · ${e.location.length > 40 ? `${e.location.slice(0, 40)}…` : e.location}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {openEvent && <EventModal event={openEvent} onClose={() => setOpenEvent(null)} />}
    </div>
  );
};

const EventModal = ({ event, onClose }) => {
  const isUrl = /^https?:\/\//i.test(event.location || '');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800" style={{ borderTop: `4px solid ${event.color}` }}>
          <h3 className="text-base font-black text-gray-900 dark:text-gray-100">{event.title}</h3>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(event.allDay ? `${event.startDay}T12:00:00` : event.start).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
            {' · '}{eventTimeLabel(event)}
          </p>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mt-1">
            {event.calendarName}{event.busy !== 'busy' && ` · ${event.busy}`}{event.recurring && ' · recurring'}
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-3 text-sm">
          {event.location && (
            <Row label="Where">
              {isUrl ? <a href={event.location} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline break-all">{event.location}</a> : event.location}
            </Row>
          )}
          {event.organizer && <Row label="Organizer">{event.organizer}</Row>}
          {event.attendees?.length > 0 && <Row label={`Attendees (${event.attendees.length})`}>{event.attendees.join(', ')}</Row>}
          {event.description && (
            <Row label="Notes"><div className="whitespace-pre-wrap break-words text-gray-600 dark:text-gray-300">{event.description}</div></Row>
          )}
        </div>
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
          <button onClick={onClose} className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700">Close</button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-0.5">{label}</div>
    <div className="text-gray-700 dark:text-gray-200">{children}</div>
  </div>
);

export default Calendar;
